import type {
  PullRequestActivity,
  PullRequestDetail,
  PullRequestReviewThread,
} from "@cmmd-forge/contracts";

export type PullRequestTimelineEntry =
  | { readonly id: "opened"; readonly kind: "opened"; readonly at: string }
  | {
      readonly id: `commit:${string}`;
      readonly kind: "commit";
      readonly at: string;
      readonly commit: PullRequestActivity["commits"][number];
    }
  | {
      readonly id: `comment:${string}`;
      readonly kind: "comment";
      readonly at: string;
      readonly comment: PullRequestActivity["comments"][number];
    }
  | {
      readonly id: `thread:${string}`;
      readonly kind: "thread";
      readonly at: string | null;
      readonly thread: PullRequestReviewThread;
    }
  | { readonly id: "merged" | "closed"; readonly kind: "terminal"; readonly at: string };

/**
 * Builds the single chronological stream used by the Timeline tab. A review thread is one
 * actionable conversation entry; its comments are not repeated as flat activity remarks.
 */
export function buildPullRequestTimelineEntries(
  detail: Pick<PullRequestDetail, "createdAt" | "mergedAt" | "closedAt">,
  activity: PullRequestActivity,
  order: "newest" | "oldest",
): ReadonlyArray<PullRequestTimelineEntry> {
  const threadCommentIds = new Set(
    activity.reviewThreads.flatMap((thread) => thread.comments.map((comment) => comment.id)),
  );
  const entries: PullRequestTimelineEntry[] = [
    { id: "opened", kind: "opened", at: detail.createdAt },
    ...activity.commits.map((commit) => ({
      id: `commit:${commit.oid}` as const,
      kind: "commit" as const,
      at: commit.committedDate,
      commit,
    })),
    ...activity.comments
      .filter((comment) => !threadCommentIds.has(comment.id))
      .map((comment) => ({
        id: `comment:${comment.id}` as const,
        kind: "comment" as const,
        at: comment.createdAt,
        comment,
      })),
    ...activity.reviewThreads.map((thread) => ({
      id: `thread:${thread.id}` as const,
      kind: "thread" as const,
      at: latestThreadCommentAt(thread),
      thread,
    })),
    ...terminalEntry(detail),
  ];
  return orderTimelineEntries(entries, order);
}

function terminalEntry(
  detail: Pick<PullRequestDetail, "mergedAt" | "closedAt">,
): ReadonlyArray<PullRequestTimelineEntry> {
  if (detail.mergedAt !== null) return [{ id: "merged", kind: "terminal", at: detail.mergedAt }];
  if (detail.closedAt !== null) return [{ id: "closed", kind: "terminal", at: detail.closedAt }];
  return [];
}

function latestThreadCommentAt(thread: PullRequestReviewThread): string | null {
  let latest: string | null = null;
  let latestInstant = Number.NEGATIVE_INFINITY;
  for (const comment of thread.comments) {
    const at = Date.parse(comment.createdAt);
    if (Number.isNaN(at) || at <= latestInstant) continue;
    latest = comment.createdAt;
    latestInstant = at;
  }
  return latest;
}

function orderTimelineEntries(
  entries: ReadonlyArray<PullRequestTimelineEntry>,
  order: "newest" | "oldest",
): ReadonlyArray<PullRequestTimelineEntry> {
  return entries
    .map((entry, index) => ({
      entry,
      index,
      instant: entry.at === null ? null : Date.parse(entry.at),
    }))
    .toSorted((left, right) => compareTimelineEntries(left, right, order))
    .map(({ entry }) => entry);
}

function compareTimelineEntries(
  left: { readonly instant: number | null; readonly index: number },
  right: { readonly instant: number | null; readonly index: number },
  order: "newest" | "oldest",
): number {
  if (left.instant === null || Number.isNaN(left.instant)) return 1;
  if (right.instant === null || Number.isNaN(right.instant)) return -1;
  const byInstant = left.instant - right.instant;
  if (byInstant === 0) return left.index - right.index;
  return order === "oldest" ? byInstant : -byInstant;
}
